import Phaser from 'phaser'
import { onFx, type AnomalyKind, type FxEvent } from './fxBridge'

const COLORS = {
  ink: 0x070a12,
  grid: 0x162034,
  cyan: 0x62f6dd,
  violet: 0xa78bfa,
  amber: 0xffc857,
  danger: 0xff4d6d,
  pale: 0xd8fff8,
} as const

export class ContainmentScene extends Phaser.Scene {
  private anomaly: AnomalyKind = 'thread'
  private anomalyIndex = 0
  private elapsed = 0
  private intensity = 1
  private targetIntensity = 1
  private reducedMotion = false
  private status: 'idle' | 'scan' | 'stable' | 'breach' = 'idle'
  private unsubscribe: (() => void) | undefined
  private background!: Phaser.GameObjects.Graphics
  private specimen!: Phaser.GameObjects.Graphics
  private overlay!: Phaser.GameObjects.Graphics
  private scanLine!: Phaser.GameObjects.Rectangle
  private traitText!: Phaser.GameObjects.Text
  private statusText!: Phaser.GameObjects.Text

  constructor() {
    super('containment')
  }

  create(): void {
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    this.background = this.add.graphics()
    this.specimen = this.add.graphics()
    this.overlay = this.add.graphics()
    this.scanLine = this.add.rectangle(360, 120, 520, 2, COLORS.cyan, 0)
    this.traitText = this.add.text(360, 690, '', {
      color: '#d8fff8',
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
      fontSize: '18px',
      align: 'center',
      wordWrap: { width: 560 },
    }).setOrigin(0.5)
    this.statusText = this.add.text(360, 132, '관측 / 01', {
      color: '#62f6dd',
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
      fontSize: '15px',
      letterSpacing: 2,
    }).setOrigin(0.5)

    this.drawBackground()
    this.unsubscribe = onFx((event) => this.handleFx(event))
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.unsubscribe?.())
  }

  update(_time: number, delta: number): void {
    if (!this.reducedMotion) this.elapsed += delta / 1000
    this.intensity = this.reducedMotion
      ? this.targetIntensity
      : this.intensity + (this.targetIntensity - this.intensity) * Math.min(1, delta / 220)
    this.drawSpecimen()
    this.drawOverlay()
  }

  private handleFx(event: FxEvent): void {
    if (event.type === 'reset') {
      this.anomaly = 'thread'
      this.anomalyIndex = 0
      this.status = 'idle'
      this.targetIntensity = 1
      this.traitText.setText('')
      this.statusText.setText('관측 / 01').setColor('#62f6dd')
      return
    }

    if (event.type === 'stage') {
      this.anomaly = event.anomaly
      this.anomalyIndex = event.index
      this.status = 'idle'
      this.targetIntensity = 1
      this.traitText.setText('')
      this.statusText
        .setText(`관측 / 0${event.index + 1}`)
        .setColor(event.anomaly === 'mirror' ? '#a78bfa' : '#62f6dd')
      return
    }

    if (event.type === 'scan') {
      this.status = 'scan'
      this.targetIntensity = 1.35
      this.scanLine.setAlpha(0.85).setY(160)
      this.tweens.killTweensOf(this.scanLine)
      if (this.reducedMotion) {
        this.scanLine.setY(650).setAlpha(0.15)
      } else {
        this.tweens.add({
          targets: this.scanLine,
          y: 650,
          alpha: { from: 0.9, to: 0.15 },
          duration: 2400,
          ease: 'Sine.easeInOut',
        })
      }
      this.traitText.setText('의미 스펙트럼을 분리하는 중…')
      return
    }

    this.status = event.type
    this.targetIntensity = event.type === 'stable' ? 0.35 : 2.1
    this.scanLine.setAlpha(0)
    this.traitText.setText(event.traits.map((trait) => `⌁ ${trait}`).join('   '))

    if (this.reducedMotion) return

    if (event.type === 'breach') {
      this.cameras.main.shake(360, 0.008)
      this.cameras.main.flash(130, 255, 50, 90, false)
    } else {
      this.cameras.main.flash(180, 98, 246, 221, false)
    }
  }

  private drawBackground(): void {
    const graphics = this.background
    graphics.clear()
    graphics.fillStyle(COLORS.ink, 1)
    graphics.fillRect(0, 0, 720, 820)

    graphics.lineStyle(1, COLORS.grid, 0.75)
    for (let x = 40; x < 720; x += 40) graphics.lineBetween(x, 0, x, 820)
    for (let y = 20; y < 820; y += 40) graphics.lineBetween(0, y, 720, y)

    graphics.lineStyle(1, COLORS.cyan, 0.22)
    graphics.strokeRoundedRect(74, 154, 572, 506, 28)
    graphics.strokeCircle(360, 390, 224)
    graphics.strokeCircle(360, 390, 176)

    for (let index = 0; index < 24; index += 1) {
      const angle = (Math.PI * 2 * index) / 24
      const inner = 230
      const outer = index % 3 === 0 ? 246 : 238
      graphics.lineBetween(
        360 + Math.cos(angle) * inner,
        390 + Math.sin(angle) * inner,
        360 + Math.cos(angle) * outer,
        390 + Math.sin(angle) * outer,
      )
    }
  }

  private drawSpecimen(): void {
    const graphics = this.specimen
    graphics.clear()

    const baseColor = this.status === 'breach'
      ? COLORS.danger
      : this.anomaly === 'mirror'
        ? COLORS.violet
        : COLORS.cyan
    const alpha = 0.62 + Math.sin(this.elapsed * 2.4) * 0.12

    if (this.anomaly === 'thread') this.drawThread(graphics, baseColor, alpha)
    if (this.anomaly === 'bell') this.drawBell(graphics, baseColor, alpha)
    if (this.anomaly === 'mirror') this.drawMirror(graphics, baseColor, alpha)
  }

  private drawThread(graphics: Phaser.GameObjects.Graphics, color: number, alpha: number): void {
    const growth = 1 + this.intensity * 0.12
    for (let strand = 0; strand < 13; strand += 1) {
      const phase = strand * 0.83
      graphics.lineStyle(strand % 4 === 0 ? 3 : 1.4, color, alpha * (0.55 + strand / 30))
      graphics.beginPath()
      for (let point = 0; point <= 28; point += 1) {
        const t = point / 28
        const angle = t * Math.PI * 4.2 + phase + Math.sin(this.elapsed * 0.7 + strand) * 0.2
        const radius = (18 + t * 178 * growth) * (this.status === 'stable' ? 1 - t * 0.55 : 1)
        const wobble = Math.sin(t * 20 + this.elapsed * (1.1 + strand * 0.02)) * 11 * this.intensity
        const x = 360 + Math.cos(angle) * (radius + wobble)
        const y = 390 + Math.sin(angle) * (radius * 0.72 + wobble)
        if (point === 0) graphics.moveTo(x, y)
        else graphics.lineTo(x, y)
      }
      graphics.strokePath()
    }
  }

  private drawBell(graphics: Phaser.GameObjects.Graphics, color: number, alpha: number): void {
    const pulse = Math.sin(this.elapsed * 5.4) * 7 * this.intensity
    graphics.lineStyle(3, color, alpha)
    graphics.beginPath()
    graphics.moveTo(300, 492)
    graphics.lineTo(308 - pulse, 448)
    graphics.lineTo(316, 378)
    graphics.lineTo(312, 335)
    graphics.lineTo(332, 312)
    graphics.lineTo(360, 300 - pulse)
    graphics.lineTo(388, 312)
    graphics.lineTo(408, 335)
    graphics.lineTo(404, 378)
    graphics.lineTo(412 + pulse, 448)
    graphics.lineTo(420, 492)
    graphics.closePath()
    graphics.strokePath()
    graphics.strokeCircle(360, 510, 16)

    for (let wave = 0; wave < 5; wave += 1) {
      const radius = 88 + wave * 34 + Math.sin(this.elapsed * 3.4 - wave) * 12 * this.intensity
      graphics.lineStyle(1.4, color, Math.max(0.08, alpha - wave * 0.11))
      graphics.strokeEllipse(360, 395, radius * 2, radius * 1.15)
    }
  }

  private drawMirror(graphics: Phaser.GameObjects.Graphics, color: number, alpha: number): void {
    const points = 7
    const spin = Math.sin(this.elapsed * 0.6) * 0.05
    graphics.lineStyle(3, color, alpha)
    graphics.beginPath()
    for (let index = 0; index <= points; index += 1) {
      const angle = -Math.PI / 2 + (Math.PI * 2 * index) / points + spin
      const radius = index % 2 === 0 ? 174 : 160
      const x = 360 + Math.cos(angle) * radius
      const y = 390 + Math.sin(angle) * radius
      if (index === 0) graphics.moveTo(x, y)
      else graphics.lineTo(x, y)
    }
    graphics.closePath()
    graphics.strokePath()

    graphics.lineStyle(1.2, color, alpha * 0.5)
    for (let shard = 0; shard < 12; shard += 1) {
      const angle = (Math.PI * 2 * shard) / 12 + this.elapsed * 0.04
      const reach = 45 + (shard % 4) * 28 * this.intensity
      graphics.lineBetween(
        360 + Math.cos(angle) * 18,
        390 + Math.sin(angle) * 18,
        360 + Math.cos(angle) * reach,
        390 + Math.sin(angle) * reach,
      )
    }

    const offset = Math.sin(this.elapsed * 2) * 8 * this.intensity
    graphics.fillStyle(color, 0.06 + this.intensity * 0.03)
    graphics.fillTriangle(270 + offset, 520, 450 - offset, 520, 360, 250)
  }

  private drawOverlay(): void {
    const graphics = this.overlay
    graphics.clear()
    const scanAlpha = this.status === 'scan' ? 0.16 + Math.sin(this.elapsed * 8) * 0.06 : 0.04
    const color = this.status === 'breach' ? COLORS.danger : COLORS.cyan
    graphics.fillStyle(color, Math.max(0.02, scanAlpha))
    for (let y = 0; y < 820; y += 6) graphics.fillRect(0, y, 720, 1)

    const indicatorColor = this.status === 'stable'
      ? COLORS.cyan
      : this.status === 'breach'
        ? COLORS.danger
        : COLORS.amber
    graphics.fillStyle(indicatorColor, 0.8)
    graphics.fillCircle(112, 88, 4)
    graphics.fillCircle(608, 88, 4)
    if (this.anomalyIndex === 2) {
      graphics.lineStyle(1, COLORS.violet, 0.25)
      graphics.strokeRoundedRect(82, 128, 556, 524, 24)
    }
  }
}
